import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import {
  Box,
  Button,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import apiService from "../../../../../../service/Api";
import {
  getDefaultTemplateId,
  getTemplatesForService,
} from "../templates/registry";
import type { TemplateDefinition } from "../templates/types";

interface Props {
  open: boolean;
  onClose: () => void;
  serviceType: string;
  streamId: number;
  currentTemplateId?: string | null;
  onConfirm: (templateId: string) => void;
}

export default function TemplateSelectorDialog({
  open,
  onClose,
  serviceType,
  streamId,
  currentTemplateId,
  onConfirm,
}: Props) {
  const theme = useTheme();
  const templates = getTemplatesForService(serviceType);
  const defaultId = currentTemplateId ?? getDefaultTemplateId(serviceType);
  const [selected, setSelected] = useState<string>(defaultId);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        template_id: selected,
      });
      showSuccessToast("Template saved!");
      onConfirm(selected);
      onClose();
    } catch {
      showErrorToast(
        "Failed to save template. Please try again.",
        "template-save-error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Choose a Template
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {templates.map((tpl: TemplateDefinition) => {
            const isSelected = selected === tpl.id;
            const isCurrent =
              (currentTemplateId ?? getDefaultTemplateId(serviceType)) ===
              tpl.id;

            return (
              <Grid key={tpl.id} size={{ xs: 12, sm: 6 }}>
                <Box
                  onClick={() => setSelected(tpl.id)}
                  sx={{
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderRadius: 2,
                    p: 1.5,
                    cursor: "pointer",
                    position: "relative",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: isSelected
                      ? `0 0 0 3px ${theme.palette.primary.main}22`
                      : "none",
                    "&:hover": {
                      borderColor: isSelected
                        ? "primary.main"
                        : "primary.light",
                    },
                  }}
                >
                  {/* Badges */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 0.5,
                    }}
                  >
                    {isCurrent && (
                      <Chip
                        label="Current"
                        size="small"
                        color="default"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                    {isSelected && (
                      <CheckCircleRounded
                        color="primary"
                        sx={{ fontSize: 20 }}
                      />
                    )}
                  </Box>

                  <CardMedia
                    component="img"
                    image={tpl.thumbnail_url}
                    alt={tpl.name}
                    sx={{
                      width: "100%",
                      height: 180,
                      objectFit: "cover",
                      borderRadius: 1,
                      bgcolor: "grey.100",
                    }}
                  />

                  {/* Info */}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography fontWeight={700} fontSize={14}>
                      {tpl.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tpl.layoutDescription}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {saving ? "Saving…" : "Save Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
